import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useFormik } from 'formik';
import { useSelector, useDispatch } from 'react-redux';
import Button from 'products/nomad_insurance/components/Button';
import { stripeDataObjectConverter } from 'products/nomad_insurance/utils';
import { useElements, useStripe, CardCvcElement } from '@stripe/react-stripe-js';
import { fetchContracts } from 'products/nomad_insurance/state/contract';
import config from 'api/config';
import TermsModal from '../TermsModal';
import validation from './validation';
import {
  StyledBillingAddress,
  StyledCreditCard,
  ErrorMessage,
  FeedbackText,
  StyledFeedback
} from './styled';
import { verifyCardholder } from '../../state/cardholderVerification';
import { pollPaymentStatus } from '../../state/paymentStatus';
import { Terms } from '../../styled';
import { Title, InfoText, NextRow } from '../../../../../styled';
import { trackHubSpotEvent } from '../../../../../utils/hubspot/hubspot-custom-events';

const PaymentDetails = () => {
  const dispatch = useDispatch();
  const { hasEndDate } = useSelector(
    (state) => state.nomadInsurance.purchaseInsurance.coverage
  );

  const { hasAllowedAutoRenewal } = useSelector(
    (state) => state.nomadInsurance.planholder.planholder
  );

  const { isLoading: paymentStatusLoading } = useSelector(
    (state) => state.nomadInsurance.purchaseInsurance.independencePayment.paymentStatus
  );

  const {
    isSucceeded: isPollPaymentStatusSucceeded,
    isFailed: isPollPaymentStatusFailed
  } = useSelector(
    (state) =>
      state.nomadInsurance.purchaseInsurance.independencePayment.paymentStatus
        .pollPaymentStatus
  );

  const { clientSecret, paymentTypeID } = useSelector(
    (state) => state.nomadInsurance.purchaseInsurance.independencePayment.paymentIntent
  );
  const {
    isLoading: cardholderVerificationLoading,
    isCardholderValid,
    isVerificationCompleted
  } = useSelector(
    (state) =>
      state.nomadInsurance.purchaseInsurance.independencePayment.cardholderVerification
  );

  const { data: planholder } = useSelector((state) => state.app.profile);
  const [isProcessing, setIsProcessing] = useState(false);
  const [stripeError, setStripeError] = useState(null);

  const [showTermsModal, setShowTermsModal] = useState(false);

  const stripe = useStripe();
  const elements = useElements();

  const handleSubmit = ({ creditCardHolderName }) => {
    dispatch(verifyCardholder(creditCardHolderName, planholder?.dateOfBirth));
  };

  const formik = useFormik({
    initialValues: useMemo(
      () => ({
        creditCardHolderName: `${planholder?.firstName} ${planholder?.lastName}`,
        address: {
          line1: planholder.address.line1 || '',
          line2: planholder.address.line2 || '',
          city: planholder.address.city || '',
          country: planholder.address.country || '',
          state: planholder.address.state || '',
          zipCode: planholder.address.zipCode || ''
        }
      }),
      [planholder.address] // eslint-disable-line
    ),
    validationSchema: validation,
    onSubmit: handleSubmit,
    validateOnMount: true
  });

  const confirmPayment = useCallback(async () => {
    if (!stripe || !elements) {
      return;
    }
    const { address, creditCardHolderName } = formik.values;

    const { city, country, line1, line2, zipCode, state } = address;

    const billingDetails = {
      name: creditCardHolderName,
      city,
      country,
      line1,
      line2,
      postalCode: zipCode,
      state
    };

    const cardElement = elements.getElement(CardCvcElement);
    const stripeDataObject = stripeDataObjectConverter(billingDetails, cardElement);

    setIsProcessing(true);
    setStripeError(null);

    const { paymentIntent, error } = await stripe.confirmCardPayment(
      clientSecret,
      stripeDataObject
    );

    if (error) {
      setStripeError(error);
    } else if (paymentIntent && paymentIntent.status === 'succeeded') {
      void trackHubSpotEvent('purchased', planholder.email);
      dispatch(pollPaymentStatus(paymentTypeID));
    }

    setIsProcessing(false);
  }, [formik]);

  useEffect(() => {
    if (isPollPaymentStatusSucceeded) {
      dispatch(fetchContracts());

      window.location.href = `${config.appUrl}/nomad-insurance-essential/thank-you`;
    }
  }, [isPollPaymentStatusSucceeded]);

  useEffect(() => {
    if (isVerificationCompleted && isCardholderValid) {
      confirmPayment();
    }
  }, [isCardholderValid, paymentTypeID, isVerificationCompleted, dispatch]);

  return (
    <>
      <TermsModal isOpen={showTermsModal} onClose={() => setShowTermsModal(false)} />
      <Title>Enter your payment information</Title>
      {!hasEndDate && (
        <InfoText>
          You will be charged for your coverage automatically every 4 weeks. You can stop
          your recurring payments in your dashboard at any time.
        </InfoText>
      )}
      {stripeError && <ErrorMessage error={stripeError} />}
      {isPollPaymentStatusFailed && (
        <ErrorMessage
          error={{
            title: 'Something went wrong',
            message:
              'Something went wrong. Please check your information and try again, and if the problem persists, contact customer service.'
          }}
        />
      )}
      {isVerificationCompleted && !isCardholderValid && (
        <ErrorMessage
          error={{
            title: 'Unsuccessful payment',
            message: `We couldn’t verify the cardholder name to complete the payment. 
                Try using a card under a different name, 
                or use the chat function to talk to someone if you need help.`
          }}
        />
      )}

      <StyledCreditCard formik={formik} />

      <StyledBillingAddress formik={formik} variant="gray" />

      {hasAllowedAutoRenewal && !hasEndDate && (
        <StyledFeedback variant="infoYellow" topAlignIcon>
          <strong>Stay covered after your plan expires</strong>
          <FeedbackText>
            After 364 days, a new insurance plan will automatically start with the same
            billing periods. We'll remind you again a few days before your current plan
            expires. You can stop your subscription anytime.
          </FeedbackText>
        </StyledFeedback>
      )}

      <Terms>
        By clicking “purchase policy”, on behalf of myself and each individual applicant
        listed, we hereby apply to become members of the SafetyWing Special Purpose Trust
        and agree to the associated Trust terms. Upon approval of our trust membership, we
        hereby apply to purchase the insurance coverage selected and agree to the
        associated purchase terms.{' '}
        <span onClick={() => setShowTermsModal(true)}>Read more...</span>
      </Terms>
      <NextRow>
        <Button
          color="confetti"
          onClick={formik.submitForm}
          disabled={
            isProcessing ||
            !formik.isValid ||
            paymentStatusLoading ||
            cardholderVerificationLoading
          }
        >
          {isProcessing || paymentStatusLoading || cardholderVerificationLoading
            ? 'Processing'
            : 'Purchase policy'}
        </Button>
      </NextRow>
    </>
  );
};

export default PaymentDetails;
